import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import api from '../api';
import './SiteScanner.css';

// ── labels / helpers (ported from Crawly lib/seo/i18n.ts) ──────────────────
const CAT_LABEL = { tech: 'בריאות טכנית', meta: 'תגיות מטא', struct: 'מבנה תוכן', img: 'תמונות', links: 'קישורים', index: 'אינדוקס', schema: 'סכמות' };
const CATS = ['tech', 'meta', 'struct', 'img', 'links', 'index', 'schema'];
// Mirror of backend CATEGORY_WEIGHTS (scoring.py). Safety net: if site_score
// comes back 0 (legacy run scored by the old saturating sum) while category
// scores are present, derive the overall from the categories instead.
const CAT_WEIGHTS = { tech: 0.25, meta: 0.20, struct: 0.15, img: 0.15, links: 0.15, index: 0.10 };
function overallScore(run) {
  if (!run) return null;
  if (run.site_score) return run.site_score;
  const cs = run.category_scores;
  if (cs) {
    let acc = 0, w = 0;
    for (const c in CAT_WEIGHTS) { const s = cs[c]; if (s != null) { acc += s * CAT_WEIGHTS[c]; w += CAT_WEIGHTS[c]; } }
    if (w > 0) return Math.round(acc / w);
  }
  return run.site_score;
}
// New Ahrefs taxonomy (error/warning/notice) + legacy aliases so historical
// runs still render correctly.
const SEV_LABEL = { error: 'שגיאה', warning: 'אזהרה', notice: 'הערה', critical: 'שגיאה', important: 'אזהרה', recommend: 'הערה' };
const SEV_TONE = { error: 'red', warning: 'orange', notice: 'blue', critical: 'red', important: 'orange', recommend: 'blue' };
const SEV_RANK = { error: 3, warning: 2, notice: 1, critical: 3, important: 2, recommend: 1 };
// helper: counts from a run, preferring the new columns
function runErr(r) { return r.error_count != null ? r.error_count : (r.critical_count || 0); }
function runWarn(r) { return r.warning_count != null ? r.warning_count : (r.important_count || 0); }
function runNotice(r) { return r.notice_count != null ? r.notice_count : (r.recommend_count || 0); }
const STATUS_LABEL = { open: 'פתוח', 'in-progress': 'בטיפול', fixed: 'תוקן', ignored: 'להתעלם' };
const STATUS_TONE = { open: 'red', 'in-progress': 'orange', fixed: 'green', ignored: 'gray' };
const STATUS_OPTS = ['open', 'in-progress', 'fixed', 'ignored'];
const RUN_STATUS = { pending: 'ממתין', running: 'רץ', cancelling: 'עוצר', cancelled: 'בוטל', done: 'הושלם', error: 'שגיאה' };
const RUN_TONE = { pending: 'gray', running: 'blue', cancelling: 'orange', cancelled: 'gray', done: 'green', error: 'red' };

function scoreColor(s) { if (s == null) return 'var(--text-muted)'; return s >= 80 ? 'var(--green)' : s >= 60 ? 'var(--orange)' : 'var(--red)'; }
const INDEX_REASON = { ok: 'אינדקסבילי', noindex: 'noindex', canonicalized: 'canonical לאחר', redirect: 'הפניה', error: 'שגיאה' };
function indexBadge(p) {
  if (p.indexable == null) return <span style={{ color: 'var(--text-muted)' }}>–</span>;
  const ok = !!p.indexable;
  const label = INDEX_REASON[p.indexability_reason] || (ok ? 'אינדקסבילי' : 'לא אינדקסבילי');
  return <span style={{ fontSize: 11, color: ok ? 'var(--green)' : 'var(--orange)' }} title={label}>{ok ? '✓ ' : '⊘ '}{label}</span>;
}
function decodePath(u) { try { return decodeURIComponent((u || '').replace(/^https?:\/\/[^/]+/, '')) || '/'; } catch { return u || ''; } }
function formatBytes(n) { if (n == null) return ''; if (n >= 1048576) return (n / 1048576).toFixed(1) + ' MB'; if (n >= 1024) return Math.round(n / 1024) + ' KB'; return n + ' B'; }
function issueMetric(i) {
  const n = i.metric_n;
  if (n == null) return i.detail || '';
  if (i.type && i.type.startsWith('img.')) return formatBytes(n);
  if (/too_long|too_short/.test(i.type)) return n + ' תווים';
  if (i.type === 'h1.multiple') return n + ' תגיות';
  if (/duplicate/.test(i.type)) return n + ' עמודים';
  if (i.type && i.type.startsWith('link.broken')) return 'סטטוס ' + n;
  return String(n);
}

function ScoreRing({ score, size = 120 }) {
  const r = (size - 16) / 2, circ = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score || 0));
  const color = scoreColor(score);
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)', flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border)" strokeWidth="8" />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="8" strokeLinecap="round"
        strokeDasharray={circ} strokeDashoffset={circ * (1 - pct / 100)} style={{ transition: 'stroke-dashoffset .6s ease' }} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fill: color, fontSize: 30, fontWeight: 700, fontFamily: 'var(--mono)' }}>
        {score == null ? '–' : score}
      </text>
    </svg>
  );
}

export default function SiteScanner() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('overview');
  const [issues, setIssues] = useState([]);
  const [pages, setPages] = useState([]);
  const [images, setImages] = useState([]);
  const [schemas, setSchemas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [singleUrl, setSingleUrl] = useState('');
  const [message, setMessage] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const pollRef = useRef(null);

  const isActive = run && ['pending', 'running', 'cancelling'].includes(run.status);

  const loadHistory = useCallback(async (pick) => {
    try {
      const data = await api.getSiteCrawls(id);
      setHistory(data);
      if (pick && data.length) setRun(data[0]);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { loadHistory(true); /* eslint-disable-next-line */ }, [id]);

  // poll while active
  useEffect(() => {
    if (!isActive) { if (pollRef.current) clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      try {
        const fresh = await api.getCrawl(run.id);
        setRun(fresh);
        if (!['pending', 'running', 'cancelling'].includes(fresh.status)) { clearInterval(pollRef.current); loadHistory(false); }
      } catch (e) { console.error(e); }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [run && run.id, isActive]);

  // load tab data for a finished run
  useEffect(() => {
    if (!run || !['done', 'cancelled', 'error'].includes(run.status)) return;
    if (tab === 'issues') api.getCrawlIssues(run.id).then(setIssues).catch(console.error);
    if (tab === 'pages') api.getCrawlPages(run.id).then(setPages).catch(console.error);
    if (tab === 'images') api.getCrawlImages(run.id).then(setImages).catch(console.error);
    if (tab === 'schemas') api.getCrawlSchemas(run.id).then(setSchemas).catch(console.error);
    // eslint-disable-next-line
  }, [run && run.id, run && run.status, tab]);

  function flash(type, text, ms = 6000) { setMessage({ type, text }); if (ms) setTimeout(() => setMessage(null), ms); }

  async function start(opts, label) {
    setStarting(true);
    try {
      const res = await api.startCrawl(id, opts);
      const fresh = await api.getCrawl(res.scan_run_id);
      setRun(fresh); setTab('overview');
      flash('info', label + ' התחילה...');
    } catch (e) { flash('error', 'שגיאה: ' + e.message); }
    setStarting(false);
  }
  const startFull = () => start({ depth_limit: 5000, only_main_domain: true }, 'סריקת אתר');
  const startSingle = () => { if (!singleUrl.trim()) return; start({ single_page: true, url: singleUrl.trim() }, 'סריקת עמוד'); setSingleUrl(''); };

  async function stop() {
    try { const r = await api.cancelCrawl(run.id); setRun({ ...run, status: r.status }); flash('info', r.status === 'cancelled' ? 'הסריקה התקועה אופסה' : 'עוצר את הסריקה...'); }
    catch (e) { flash('error', 'שגיאה: ' + e.message); }
  }

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>טוען...</div>;
  const msgTone = { success: 'green', error: 'red', info: 'blue' }[message?.type] || 'blue';

  return (
    <>
      <div className="scan-head">
        <div>
          <h2>סריקה טכנית</h2>
          <p>זחילה מקיפה של האתר — ציון בריאות, בעיות מתועדפות ופירוט לכל עמוד</p>
        </div>
        <div className="scan-start">
          {isActive ? (
            // While 'cancelling' keep the button enabled: a second click
            // force-resets a stuck/orphaned run (worker died) so the user can
            // start a new scan instead of being stranded on a disabled button.
            <button className="scan-btn danger" onClick={stop}>{run.status === 'cancelling' ? '⟲ אפס סריקה תקועה' : '⏹ עצור'}</button>
          ) : (
            <>
              <input className="scan-input" placeholder="https://example.co.il/page" value={singleUrl}
                onChange={e => setSingleUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && startSingle()} disabled={starting} />
              <button className="scan-btn ghost" onClick={startSingle} disabled={starting || !singleUrl.trim()}>עמוד בודד</button>
              <button className="scan-btn ghost" onClick={() => setShowSettings(true)} title="הגדרות סריקה">⚙ הגדרות</button>
              <button className="scan-btn primary" onClick={startFull} disabled={starting}>🔍 סרוק אתר</button>
            </>
          )}
        </div>
      </div>

      {showSettings && <SettingsPanel siteId={id} onClose={() => setShowSettings(false)} flash={flash} />}

      {message && (
        <div className="scan-msg" style={{ background: `color-mix(in srgb, var(--${msgTone}) 12%, transparent)`, border: `1px solid color-mix(in srgb, var(--${msgTone}) 30%, transparent)`, color: `var(--${msgTone})` }}>
          <span>{message.text}</span>
          <button onClick={() => setMessage(null)} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>×</button>
        </div>
      )}

      {isActive && (
        <div className="scan-prog">
          <div className="scan-prog-row">
            <span><strong>{run.pages_done || 0}</strong> / {run.pages_total || '?'} עמודים
              {run.current_step && <span style={{ color: 'var(--text-muted)', marginRight: 8 }}> · {run.current_step}</span>}</span>
            <span className="scan-prog-url">{run.current_url ? decodePath(run.current_url) : ''}</span>
          </div>
          <div className="scan-bar"><div style={{ width: (run.pages_total ? Math.min(100, run.pages_done / run.pages_total * 100) : 6) + '%' }} /></div>
        </div>
      )}

      {run && run.status === 'error' && (
        <div className="scan-card pad" style={{ color: 'var(--red)', fontSize: 13, marginBottom: 16 }}>הסריקה נכשלה: {run.error_message || 'שגיאה לא ידועה'}</div>
      )}

      {run && ['done', 'cancelled', 'error'].includes(run.status) && (
        <>
          <div className="scan-tabs">
            {[['overview', 'סקירה'], ['issues', 'בעיות'], ['pages', 'עמודים'], ['images', 'תמונות'], ['schemas', 'סכמות']].map(([k, l]) => (
              <button key={k} className={'scan-tab' + (tab === k ? ' active' : '')} onClick={() => setTab(k)}>{l}</button>
            ))}
          </div>
          {tab === 'overview' && <Overview run={run} />}
          {tab === 'issues' && <IssuesTab run={run} issues={issues} setIssues={setIssues} flash={flash} />}
          {tab === 'pages' && <PagesTab run={run} pages={pages} />}
          {tab === 'images' && <ImagesTab images={images} />}
          {tab === 'schemas' && <SchemasTab run={run} data={schemas} flash={flash} />}
        </>
      )}

      {!run && <div className="scan-card pad scan-empty">עדיין לא בוצעה סריקה. לחץ "סרוק אתר" כדי להתחיל.</div>}

      <History history={history} run={run} onPick={r => { setRun(r); setTab('issues'); window.scrollTo({ top: 0, behavior: 'smooth' }); }} />
    </>
  );
}

// ── Per-site crawl settings panel (Ahrefs-style) ───────────────────────────
const SCHEDULE_TYPES = [['daily', 'יומי'], ['weekly', 'שבועי'], ['monthly', 'חודשי']];
const _numStyle = { width: 90, padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--bg)', color: 'var(--text)' };

// Module-scope so identity is stable across renders (inputs keep focus).
function SChk({ s, set, k, label }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!s[k]} onChange={e => set(k, e.target.checked)} /> {label}
    </label>
  );
}
function SNum({ s, set, k, label, hint }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
      <span>{label} {hint && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>({hint})</span>}</span>
      <input type="number" style={_numStyle} value={s[k] ?? ''} onChange={e => set(k, e.target.value === '' ? null : Number(e.target.value))} />
    </label>
  );
}

function SettingsPanel({ siteId, onClose, flash }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getAuditSettings(siteId).then(setS).catch(e => { flash('error', 'שגיאה בטעינת הגדרות: ' + e.message); onClose(); });
    // eslint-disable-next-line
  }, [siteId]);

  function set(k, v) { setS(prev => ({ ...prev, [k]: v })); }
  async function save() {
    setSaving(true);
    try {
      const payload = { ...s, remove_url_params: typeof s.remove_url_params === 'string'
        ? s.remove_url_params.split(',').map(x => x.trim()).filter(Boolean) : (s.remove_url_params || []) };
      delete payload.site_id;
      await api.saveAuditSettings(siteId, payload);
      flash('success', 'ההגדרות נשמרו'); onClose();
    } catch (e) { flash('error', 'שגיאה בשמירה: ' + e.message); }
    setSaving(false);
  }

  const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '40px 16px' };
  const modal = { background: 'var(--bg-elevated, #fff)', color: 'var(--text)', borderRadius: 12, width: 'min(720px, 100%)', boxShadow: '0 20px 60px rgba(0,0,0,.3)', border: '1px solid var(--border)' };
  const head = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' };
  const body = { padding: 20, display: 'grid', gap: 22 };
  const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px 18px' };
  const secTitle = { fontWeight: 700, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, borderBottom: '1px solid var(--border)', paddingBottom: 4 };
  const numStyle = _numStyle;

  if (!s) return <div style={overlay}><div style={{ ...modal, padding: 40, color: 'var(--text-muted)' }}>טוען הגדרות...</div></div>;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={e => e.stopPropagation()}>
        <div style={head}>
          <h3 style={{ margin: 0, fontSize: 16 }}>⚙ הגדרות סריקה</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-muted)' }}>×</button>
        </div>
        <div style={body}>
          <div>
            <div style={secTitle}>זחילה ומשאבים</div>
            <div style={grid}>
              <SChk s={s} set={set} k="execute_javascript" label="הרצת JavaScript (SPA)" />
              <SChk s={s} set={set} k="check_images" label="בדיקת תמונות" />
              <SChk s={s} set={set} k="check_css" label="בדיקת CSS" />
              <SChk s={s} set={set} k="check_js" label="בדיקת JavaScript" />
              <SChk s={s} set={set} k="check_external_links" label="בדיקת קישורים חיצוניים" />
              <SChk s={s} set={set} k="obey_robots" label="ציות ל-robots.txt" />
              <SChk s={s} set={set} k="follow_noncanonical" label="מעקב אחרי עמודים non-canonical" />
              <SChk s={s} set={set} k="follow_nofollow" label="מעקב אחרי קישורי nofollow" />
            </div>
          </div>
          <div>
            <div style={secTitle}>מגבלות זחילה</div>
            <div style={grid}>
              <SNum s={s} set={set} k="max_pages" label="מקס׳ עמודים" hint="1–25000" />
              <SNum s={s} set={set} k="max_crawl_hours" label="מקס׳ שעות" hint="1–168" />
              <SNum s={s} set={set} k="max_depth" label="עומק קליקים" hint="0–512" />
              <SNum s={s} set={set} k="max_folder_depth" label="עומק תיקיות" hint="0–128" />
              <SNum s={s} set={set} k="max_url_length" label="אורך URL מקס׳" hint="1–4096" />
              <SNum s={s} set={set} k="max_query_params" label="מקס׳ פרמטרים" hint="0–12" />
              <SNum s={s} set={set} k="crawl_speed" label="מהירות (concurrency)" hint="1–32" />
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
                <span>User-Agent</span>
                <input style={{ ...numStyle, width: '100%' }} value={s.user_agent || ''} placeholder="ברירת מחדל" onChange={e => set('user_agent', e.target.value || null)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13, gridColumn: '1 / -1' }}>
                <span>הסרת פרמטרים מ-URL <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>(מופרד בפסיקים, למשל: utm_source, fbclid)</span></span>
                <input style={{ ...numStyle, width: '100%' }}
                  value={Array.isArray(s.remove_url_params) ? s.remove_url_params.join(', ') : (s.remove_url_params || '')}
                  onChange={e => set('remove_url_params', e.target.value)} />
              </label>
            </div>
          </div>
          <div>
            <div style={secTitle}>תזמון אוטומטי והתראות</div>
            <div style={grid}>
              <SChk s={s} set={set} k="scheduled_audit_enabled" label="סריקה מתוזמנת" />
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 13 }}>
                <span>תדירות</span>
                <select style={{ ...numStyle, width: 'auto' }} value={s.schedule_type || 'weekly'} onChange={e => set('schedule_type', e.target.value)}>
                  {SCHEDULE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </label>
              <SNum s={s} set={set} k="schedule_hour" label="שעת ריצה" hint="0–23" />
              <SChk s={s} set={set} k="alert_on_new_errors" label="התראת מייל על שגיאות חדשות" />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--border)' }}>
          <button className="scan-btn ghost" onClick={onClose}>ביטול</button>
          <button className="scan-btn primary" onClick={save} disabled={saving}>{saving ? 'שומר...' : 'שמירה'}</button>
        </div>
      </div>
    </div>
  );
}

function Overview({ run }) {
  const counts = [['שגיאות', runErr(run), 'red'], ['אזהרות', runWarn(run), 'orange'], ['הערות', runNotice(run), 'blue']];
  const score = overallScore(run);
  const headline = score >= 80 ? 'בריאות מצוינת' : score >= 60 ? 'דרושים שיפורים' : 'דורש טיפול דחוף';
  return (
    <div>
      <div className="scan-card pad scan-hero" style={{ marginBottom: 16 }}>
        <ScoreRing score={score} />
        <div className="meta">
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, color: scoreColor(score) }}>{score == null ? 'אין ציון' : headline}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>{run.pages_done} עמודים נסרקו · {run.duration_sec}s{run.single_page ? ' · עמוד בודד' : ''}{run.status === 'cancelled' ? ' · בוטלה' : ''}</div>
          <div style={{ display: 'flex', gap: 18 }}>
            {counts.map(([l, n, t]) => (<div key={l}><span style={{ color: `var(--${t === 'blue' ? 'accent' : t})`, fontWeight: 700, fontFamily: 'var(--mono)', fontSize: 20 }}>{n || 0}</span><span style={{ color: 'var(--text-muted)', fontSize: 12, marginRight: 4 }}>{l}</span></div>))}
          </div>
          {run.prev_run_id != null && (run.new_issues_count != null || run.fixed_issues_count != null) && (
            <div style={{ marginTop: 10, fontSize: 12.5, display: 'flex', gap: 14 }}>
              <span style={{ color: 'var(--text-muted)' }}>מאז הסריקה הקודמת:</span>
              <span style={{ color: 'var(--red)' }}>+{run.new_issues_count || 0} בעיות חדשות</span>
              <span style={{ color: 'var(--green)' }}>−{run.fixed_issues_count || 0} תוקנו</span>
              {(run.pages_added || run.pages_removed) ? <span style={{ color: 'var(--text-muted)' }}>· עמודים: +{run.pages_added || 0} / −{run.pages_removed || 0}</span> : null}
            </div>
          )}
        </div>
      </div>
      <div className="scan-cats">
        {CATS.map(c => { const s = run.category_scores ? run.category_scores[c] : null; return (<div className="scan-cat" key={c}><div className="lbl">{CAT_LABEL[c]}</div><div className="val" style={{ color: scoreColor(s) }}>{s == null ? '–' : s}</div></div>); })}
      </div>
      <div className="scan-flags">
        {!run.single_page && <><Flag ok={run.has_sitemap} label="Sitemap" /><Flag ok={run.has_robots} label="robots.txt" /><Flag ok={!run.robots_blocks_site} label="לא חסום לסריקה" /></>}
        {run.pagespeed_score != null && <span className="scan-flag">PageSpeed: <strong style={{ color: scoreColor(run.pagespeed_score) }}>{run.pagespeed_score}</strong></span>}
      </div>
    </div>
  );
}
function Flag({ ok, label }) { return <span className="scan-flag"><span style={{ color: ok ? 'var(--green)' : 'var(--red)' }}>{ok ? '✓' : '✗'}</span> {label}</span>; }

function IssuesTab({ run, issues, setIssues, flash }) {
  const [sev, setSev] = useState('all');
  const [cat, setCat] = useState('all');
  const [stat, setStat] = useState('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'desc' });
  const [selected, setSelected] = useState(new Set());

  const toggleSort = k => setSort(s => s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' });
  const caret = k => sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const count = s => issues.filter(i => i.severity === s).length;
  const catCount = c => issues.filter(i => i.category === c).length;

  let view = issues.filter(i =>
    (sev === 'all' || i.severity === sev) && (cat === 'all' || i.category === cat) && (stat === 'all' || i.status === stat) &&
    (!q || (i.url || '').toLowerCase().includes(q.toLowerCase()) || (i.ref || '').toLowerCase().includes(q.toLowerCase()) || (i.title || '').includes(q) || decodePath(i.url).toLowerCase().includes(q.toLowerCase())));
  if (sort.key) {
    const d = sort.dir === 'asc' ? 1 : -1;
    view = [...view].sort((a, b) => ((sort.key === 'severity' ? SEV_RANK[a.severity] : (a.metric_n ?? -1)) - (sort.key === 'severity' ? SEV_RANK[b.severity] : (b.metric_n ?? -1))) * d);
  }

  function toggle(idd) { const n = new Set(selected); if (n.has(idd)) n.delete(idd); else n.add(idd); setSelected(n); }
  async function addToChecklist() {
    if (!selected.size) return;
    try { const r = await api.crawlIssuesToChecklist(run.id, [...selected]); flash('success', `נוספו ${r.created} משימות ל-Checklist`); setSelected(new Set()); }
    catch (e) { flash('error', 'שגיאה: ' + e.message); }
  }
  async function setStatus(issueId, value) {
    setIssues(list => list.map(i => i.id === issueId ? { ...i, status: value } : i));
    try { await api.updateCrawlIssueStatus(run.id, issueId, value); } catch (e) { console.error(e); }
  }

  return (
    <div>
      <div className="scan-filters">
        <div className="scan-pills">
          {[['all', 'הכל', null, issues.length], ['error', 'שגיאות', 'red', count('error')], ['warning', 'אזהרות', 'orange', count('warning')], ['notice', 'הערות', 'blue', count('notice')]].map(([k, l, dot, n]) => (
            <button key={k} className={'scan-pill' + (sev === k ? ' on' : '')} onClick={() => setSev(k)}>{dot && <span className={'dot ' + dot} />}{l} · {n}</button>
          ))}
        </div>
        <div className="scan-pills">
          <button className={'scan-pill' + (cat === 'all' ? ' on' : '')} onClick={() => setCat('all')}>הכל</button>
          {CATS.map(c => <button key={c} className={'scan-pill' + (cat === c ? ' on' : '')} onClick={() => setCat(c)}>{CAT_LABEL[c]} · {catCount(c)}</button>)}
        </div>
        <div className="scan-pills">
          {[['all', 'כל הסטטוסים'], ...STATUS_OPTS.map(s => [s, STATUS_LABEL[s]])].map(([k, l]) => (
            <button key={k} className={'scan-pill' + (stat === k ? ' on' : '')} onClick={() => setStat(k)}>{l}</button>
          ))}
        </div>
        <div className="scan-search"><input placeholder="חיפוש לפי URL / נכס / סוג" value={q} onChange={e => setQ(e.target.value)} /></div>
        {selected.size > 0 && <button className="scan-btn primary" onClick={addToChecklist}>+ הוסף {selected.size} למשימות</button>}
      </div>

      <div className="scan-card" style={{ overflow: 'hidden' }}>
        <table className="scan-table">
          <thead><tr>
            <th style={{ width: 30 }}></th>
            <th className="sortable" style={{ width: 80 }} onClick={() => toggleSort('severity')}>עדיפות{caret('severity')}</th>
            <th>בעיה</th>
            <th style={{ width: 100 }}>עמוד / נכס</th>
            <th className="sortable" style={{ width: 110 }} onClick={() => toggleSort('metric')}>מדד{caret('metric')}</th>
            <th style={{ width: 130 }}>קטגוריה</th>
            <th style={{ width: 120 }}>סטטוס</th>
          </tr></thead>
          <tbody>
            {view.map(i => (
              <tr key={i.id}>
                <td><input type="checkbox" checked={selected.has(i.id)} onChange={() => toggle(i.id)} style={{ accentColor: 'var(--accent)' }} /></td>
                <td><span className={'chip ' + SEV_TONE[i.severity]}>{SEV_LABEL[i.severity]}</span></td>
                <td><div style={{ fontWeight: 600 }}>{i.title}{i.detail ? <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}> — {i.detail}</span> : null}</div>{i.recommendation && <div style={{ color: 'var(--text-secondary)', fontSize: 12, marginTop: 2 }}>{i.recommendation}</div>}</td>
                <td>{(i.ref || i.url) ? <a className="scan-url" href={i.ref || i.url} target="_blank" rel="noreferrer">{decodePath(i.ref || i.url)}</a> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td className="scan-num">{issueMetric(i) || '—'}</td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{CAT_LABEL[i.category]}</td>
                <td>
                  <span className={'chip ' + STATUS_TONE[i.status]}><span className={'dot ' + STATUS_TONE[i.status]} />
                    <select value={i.status} onChange={e => setStatus(i.id, e.target.value)}>{STATUS_OPTS.map(s => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}</select>
                  </span>
                </td>
              </tr>
            ))}
            {view.length === 0 && <tr><td colSpan={7} className="scan-empty">לא נמצאו בעיות בסינון הנוכחי 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PagesTab({ run, pages }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState({ key: null, dir: 'desc' });
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [linkView, setLinkView] = useState(null); // {dir, rows}
  const toggleSort = k => setSort(s => s.key === k ? { key: k, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key: k, dir: 'desc' });
  const caret = k => sort.key === k ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';
  const links = p => (p.internal_links_count || 0) + (p.external_links_count || 0);

  let view = pages.filter(p => !q || decodePath(p.url).toLowerCase().includes(q.toLowerCase()));
  if (sort.key) { const d = sort.dir === 'asc' ? 1 : -1; const val = p => sort.key === 'images' ? (p.images_count || 0) : links(p); view = [...view].sort((a, b) => (val(a) - val(b)) * d); }

  async function expand(p) {
    if (open === p.id) { setOpen(null); setDetail(null); setLinkView(null); return; }
    try { const d = await api.getCrawlPage(run.id, p.id); setDetail(d); setOpen(p.id); setLinkView(null); } catch (e) { console.error(e); }
  }
  async function loadLinks(pageId, dir) {
    try { const rows = await api.getPageLinks(run.id, pageId, dir); setLinkView({ dir, rows }); } catch (e) { console.error(e); }
  }

  return (
    <div>
      <div className="scan-filters"><div className="scan-search"><input placeholder="חיפוש עמוד לפי URL" value={q} onChange={e => setQ(e.target.value)} /></div></div>
      <div className="scan-card" style={{ overflow: 'auto' }}>
        <table className="scan-table">
          <thead><tr>
            <th>URL</th>
            <th style={{ width: 90, textAlign: 'center' }}>אינדקס</th>
            <th style={{ width: 50, textAlign: 'center' }}>H1</th>
            <th className="sortable" style={{ width: 80, textAlign: 'center' }} onClick={() => toggleSort('images')}>תמונות{caret('images')}</th>
            <th className="sortable" style={{ width: 80, textAlign: 'center' }} onClick={() => toggleSort('links')}>קישורים{caret('links')}</th>
          </tr></thead>
          <tbody>
            {view.map(p => (
              <React.Fragment key={p.id}>
                <tr onClick={() => expand(p)} style={{ cursor: 'pointer' }}>
                  <td><span className="scan-url">{decodePath(p.url)}</span></td>
                  <td style={{ textAlign: 'center' }}>{indexBadge(p)}</td>
                  <td style={{ textAlign: 'center', color: p.h1 ? 'var(--green)' : 'var(--red)' }}>{p.h1 ? '✓' : '✗'}</td>
                  <td style={{ textAlign: 'center', fontFamily: 'var(--mono)' }}>{p.images_count || 0}{(p.images_without_alt || 0) > 0 && <span style={{ color: 'var(--red)', fontSize: 10 }}> ({p.images_without_alt})</span>}</td>
                  <td style={{ textAlign: 'center', fontFamily: 'var(--mono)' }}>{links(p)}</td>
                </tr>
                {open === p.id && detail && (
                  <tr><td colSpan={5} style={{ background: 'var(--bg)' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12, fontSize: 12, padding: '4px 0' }}>
                      <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>META TITLE ({detail.meta_title_length || 0})</div><div style={{ color: detail.meta_title ? 'var(--text)' : 'var(--red)' }}>{detail.meta_title || 'חסר!'}</div></div>
                      <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>META DESCRIPTION</div><div style={{ color: detail.meta_description ? 'var(--text)' : 'var(--red)' }}>{detail.meta_description || 'חסר!'}</div></div>
                      <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>H1</div><div style={{ color: detail.h1 ? 'var(--text)' : 'var(--red)' }}>{detail.h1 || 'חסר!'}</div></div>
                      <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>CANONICAL</div><div className="scan-url">{detail.canonical_url || '-'}{detail.canonical_status_code ? ` (${detail.canonical_status_code})` : ''}</div></div>
                      <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>אינדקסביליות</div><div>{indexBadge(detail)}</div></div>
                      {detail.redirect_target && <div><div style={{ color: 'var(--text-muted)', fontSize: 10 }}>REDIRECT</div><div className="scan-url">{decodePath(detail.redirect_target)}{detail.redirect_loop ? ' ⚠ לולאה' : ''}</div></div>}
                    </div>
                    {(detail.issues || []).length > 0 && (
                      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {detail.issues.map(iss => <span key={iss.id} className={'chip ' + SEV_TONE[iss.severity]}>{iss.title}</span>)}
                      </div>
                    )}
                    <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button className="scan-btn ghost" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => loadLinks(p.id, 'in')}>קישורים נכנסים</button>
                      <button className="scan-btn ghost" style={{ padding: '2px 10px', fontSize: 12 }} onClick={() => loadLinks(p.id, 'out')}>קישורים יוצאים</button>
                    </div>
                    {linkView && (
                      <div style={{ marginTop: 8, maxHeight: 200, overflowY: 'auto', fontSize: 12 }}>
                        <div style={{ color: 'var(--text-muted)', fontSize: 10, marginBottom: 4 }}>{linkView.dir === 'in' ? 'נכנסים' : 'יוצאים'} ({linkView.rows.length})</div>
                        {linkView.rows.length === 0 ? <div style={{ color: 'var(--text-muted)' }}>אין</div> :
                          linkView.rows.map((l, idx) => (
                            <div key={idx} className="scan-url" style={{ padding: '1px 0' }}>
                              {decodePath(linkView.dir === 'in' ? l.src_url : l.dst_url)}
                              {l.is_nofollow && <span style={{ color: 'var(--orange)', fontSize: 10 }}> nofollow</span>}
                              {l.anchor_text && <span style={{ color: 'var(--text-muted)' }}> — {l.anchor_text}</span>}
                            </div>
                          ))}
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
            {view.length === 0 && <tr><td colSpan={4} className="scan-empty">אין עמודים</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ImagesTab({ images }) {
  const [q, setQ] = useState('');
  const probLabel = { 'img.huge': 'כבדה מאוד', 'img.heavy': 'כבדה', 'img.no_alt': 'ALT חסר' };
  const view = images.filter(im => !q || decodePath(im.src).toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="scan-filters">
        <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{images.length} תמונות בעייתיות · ממוין מהכבדה לקלה</div>
        <div className="scan-search"><input placeholder="חיפוש תמונה" value={q} onChange={e => setQ(e.target.value)} /></div>
      </div>
      <div className="scan-card" style={{ overflow: 'auto' }}>
        <table className="scan-table">
          <thead><tr><th>קובץ</th><th style={{ width: 130 }}>עמוד מקור</th><th style={{ width: 90, textAlign: 'center' }}>גודל</th><th style={{ width: 180 }}>בעיות</th></tr></thead>
          <tbody>
            {view.map((im, idx) => (
              <tr key={idx}>
                <td><a className="scan-url" href={im.src} target="_blank" rel="noreferrer">{decodePath(im.src)}</a></td>
                <td>{im.url ? <a className="scan-url" href={im.url} target="_blank" rel="noreferrer">{decodePath(im.url)}</a> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                <td style={{ textAlign: 'center', fontFamily: 'var(--mono)' }}>{im.size_bytes ? formatBytes(im.size_bytes) : '—'}</td>
                <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{(im.problems || []).map(p => <span key={p} className={'chip ' + (p === 'img.huge' ? 'red' : p === 'img.heavy' ? 'orange' : 'gray')}>{probLabel[p] || p}</span>)}</td>
              </tr>
            ))}
            {view.length === 0 && <tr><td colSpan={4} className="scan-empty">אין תמונות בעייתיות 🎉</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SchemasTab({ run, data, flash }) {
  const [q, setQ] = useState('');
  const [activeType, setActiveType] = useState(null);
  const [missingMode, setMissingMode] = useState(false);
  const [modal, setModal] = useState(null); // {page, type, mode}

  if (!data) return <div className="scan-card pad scan-empty">טוען סכמות...</div>;

  const found = data.types || [];
  const catalog = data.catalog || [];
  const richTargets = catalog.filter(c => c.rich_result);
  const labelOf = t => (catalog.find(c => c.type === t) || {}).label_he || t;
  const richSet = new Set(catalog.filter(c => c.rich_result).map(c => c.type));

  function pickWith(t) { setActiveType(t); setMissingMode(false); }
  function pickMissing(t) { setActiveType(t); setMissingMode(true); }
  function clearFilter() { setActiveType(null); setMissingMode(false); }

  let pages = data.pages || [];
  if (activeType && missingMode) pages = pages.filter(p => !(p.types || []).includes(activeType));
  else if (activeType) pages = pages.filter(p => (p.types || []).includes(activeType));
  pages = pages.filter(p => !q || decodePath(p.url).toLowerCase().includes(q.toLowerCase()));

  const firstKnownType = p => (p.types || []).find(t => catalog.some(c => c.type === t));

  return (
    <div>
      {/* coverage — which schema types exist, and on how many pages */}
      <div className="scan-filters">
        <div className="scan-pills">
          <button className={'scan-pill' + (!activeType ? ' on' : '')} onClick={clearFilter}>הכל · {data.total_pages}</button>
          {found.map(t => (
            <button key={t.type} className={'scan-pill' + (activeType === t.type && !missingMode ? ' on' : '')} onClick={() => pickWith(t.type)} title={t.type}>
              {t.rich_result && <span className="dot green" />}{t.label_he} · {t.pages_count}
            </button>
          ))}
          {found.length === 0 && <span style={{ color: 'var(--text-muted)', fontSize: 13, padding: '6px 4px' }}>לא נמצאו סכמות JSON-LD באתר</span>}
        </div>
      </div>

      {/* missing filter — flag pages lacking a rich-result schema (FAQ / Video / …) */}
      <div className="scan-filters" style={{ marginTop: -6 }}>
        <span style={{ color: 'var(--text-muted)', fontSize: 12, alignSelf: 'center' }}>עמודים ללא סכמה:</span>
        <div className="scan-pills">
          {richTargets.map(c => (
            <button key={c.type} className={'scan-pill' + (activeType === c.type && missingMode ? ' on' : '')} onClick={() => pickMissing(c.type)} title={c.type}>
              בלי {c.label_he}
            </button>
          ))}
        </div>
        <div className="scan-search"><input placeholder="חיפוש עמוד לפי URL" value={q} onChange={e => setQ(e.target.value)} /></div>
      </div>

      {activeType && (
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '4px 2px 10px' }}>
          {missingMode ? `מציג ${pages.length} עמודים ללא ${labelOf(activeType)}` : `מציג ${pages.length} עמודים עם ${labelOf(activeType)}`}
          <button className="scan-btn ghost" style={{ padding: '1px 8px', fontSize: 11, marginRight: 8 }} onClick={clearFilter}>נקה סינון</button>
        </div>
      )}

      <div className="scan-card" style={{ overflow: 'auto' }}>
        <table className="scan-table">
          <thead><tr>
            <th>URL</th>
            <th>סכמות מוטמעות</th>
            <th style={{ width: 200 }}></th>
          </tr></thead>
          <tbody>
            {pages.map(p => {
              const types = p.types || [];
              const fixType = p.invalid ? firstKnownType(p) : null;
              return (
                <tr key={p.id}>
                  <td><a className="scan-url" href={p.url} target="_blank" rel="noreferrer">{decodePath(p.url)}</a></td>
                  <td style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {types.length === 0 ? <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span> :
                      types.map(t => <span key={t} className={'chip ' + (richSet.has(t) ? 'green' : 'gray')}>{labelOf(t)}</span>)}
                    {p.invalid && <span className="chip red" title="JSON-LD לא תקין או חסר שדות חובה">לא תקין</span>}
                  </td>
                  <td style={{ textAlign: 'left' }}>
                    {missingMode && activeType && (
                      <button className="scan-btn primary" style={{ padding: '3px 10px', fontSize: 12 }}
                        onClick={() => setModal({ page: p, type: activeType, mode: 'generate' })}>✨ צור עם קלוד</button>
                    )}
                    {/* No schema on this page → let the user pick a type and generate one. */}
                    {types.length === 0 && !(missingMode && activeType) && (
                      <select className="scan-btn" defaultValue="" style={{ padding: '3px 10px', fontSize: 12 }}
                        onChange={e => { const t = e.target.value; e.target.value = ''; if (t) setModal({ page: p, type: t, mode: 'generate' }); }}>
                        <option value="" disabled>➕ צור סכמה…</option>
                        {catalog.map(c => (
                          <option key={c.type} value={c.type}>{c.label_he}{c.rich_result ? ' ★' : ''}</option>
                        ))}
                      </select>
                    )}
                    {fixType && (
                      <button className="scan-btn ghost" style={{ padding: '3px 10px', fontSize: 12, marginRight: 6 }}
                        onClick={() => setModal({ page: p, type: fixType, mode: 'fix' })}>🔧 תקן עם קלוד</button>
                    )}
                  </td>
                </tr>
              );
            })}
            {pages.length === 0 && <tr><td colSpan={3} className="scan-empty">אין עמודים בסינון הנוכחי</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && <SchemaModal run={run} info={modal} labelOf={labelOf} flash={flash} onClose={() => setModal(null)} />}
    </div>
  );
}

function SchemaModal({ run, info, labelOf, flash, onClose }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    api.generatePageSchema(run.id, info.page.id, { type: info.type, mode: info.mode })
      .then(r => { if (alive) { setResult(r); setLoading(false); } })
      .catch(e => { if (alive) { setErr(e.message); setLoading(false); } });
    return () => { alive = false; };
    // eslint-disable-next-line
  }, []);

  const jsonStr = result && result.schema ? JSON.stringify(result.schema, null, 2) : '';
  const scriptTag = jsonStr ? `<script type="application/ld+json">\n${jsonStr}\n</script>` : '';
  function copy() {
    navigator.clipboard.writeText(scriptTag).then(() => flash('success', 'הסכמה הועתקה')).catch(() => flash('error', 'ההעתקה נכשלה'));
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()} className="scan-card pad" style={{ width: 'min(720px,100%)', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700 }}>{info.mode === 'fix' ? '🔧 תיקון' : '✨ יצירת'} סכמת {labelOf(info.type)} עם קלוד</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div className="scan-url" style={{ marginBottom: 12 }}>{decodePath(info.page.url)}</div>

        {loading && <div style={{ color: 'var(--text-muted)', fontSize: 13, padding: '20px 0' }}>קלוד מנתח את העמוד ויוצר את הסכמה...</div>}
        {err && <div style={{ color: 'var(--red)', fontSize: 13 }}>שגיאה: {err}</div>}

        {result && result.schema && (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
              {result.valid
                ? <span className="chip green">סכמה תקינה</span>
                : <span className="chip orange" title={(result.missing_required || []).join(', ')}>חסרים שדות: {(result.missing_required || []).join(', ')}</span>}
              {result.cost != null && <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>עלות: ${Number(result.cost).toFixed(4)}</span>}
            </div>
            <pre style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, fontSize: 12, fontFamily: 'var(--mono)', direction: 'ltr', textAlign: 'left', overflow: 'auto', maxHeight: '45vh' }}>{scriptTag}</pre>
            <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
              <button className="scan-btn primary" onClick={copy}>📋 העתק קוד</button>
              <span style={{ color: 'var(--text-muted)', fontSize: 11, alignSelf: 'center' }}>הדבק בתוך תגית &lt;head&gt; של העמוד</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function History({ history, run, onPick }) {
  if (!history.length) return null;
  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8, color: 'var(--text-secondary)' }}>היסטוריית סריקות</div>
      <div className="scan-card" style={{ overflow: 'auto' }}>
        <table className="scan-table">
          <thead><tr><th>סריקה</th><th style={{ width: 90 }}>סטטוס</th><th style={{ width: 70 }}>ציון</th><th style={{ width: 80 }}>עמודים</th><th style={{ width: 130 }}>בעיות</th><th style={{ width: 150 }}>תאריך</th><th style={{ width: 80 }}></th></tr></thead>
          <tbody>
            {history.map(h => (
              <tr key={h.id} style={{ background: run && run.id === h.id ? 'var(--surface-hover, rgba(255,255,255,.03))' : 'transparent' }}>
                <td><span style={{ fontWeight: 600 }}>{h.single_page ? 'עמוד בודד' : 'אתר מלא'}</span><div className="scan-url">{decodePath(h.url)}</div></td>
                <td><span className={'chip ' + (RUN_TONE[h.status] || 'gray')}><span className={'dot ' + (RUN_TONE[h.status] || 'gray')} />{RUN_STATUS[h.status] || h.status}</span></td>
                <td>{(() => { const s = overallScore(h); return s != null ? <span style={{ fontWeight: 700, color: scoreColor(s), fontFamily: 'var(--mono)' }}>{s}</span> : <span style={{ color: 'var(--text-muted)' }}>—</span>; })()}</td>
                <td className="scan-num">{h.pages_done || 0}</td>
                <td style={{ fontSize: 12.5 }}><span style={{ color: 'var(--red)' }}>{runErr(h)}</span> · <span style={{ color: 'var(--orange)' }}>{runWarn(h)}</span> · <span style={{ color: 'var(--accent)' }}>{runNotice(h)}</span></td>
                <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{h.created_at ? new Date(h.created_at).toLocaleString('he-IL', { dateStyle: 'short', timeStyle: 'short' }) : ''}</td>
                <td><button className="scan-btn ghost" style={{ padding: '5px 12px', fontSize: 12 }} onClick={() => onPick(h)}>צפייה</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
